/**
 * An enumeration of possible access vectors associated with a vulnerability.
 *
 * @public
 */
export enum AccessVector {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes that the vulnerability can only be exploited with local system access.
     */
    Local = 'LOCAL',

    /**
     * Denotes that the vulnerability can only be exploited with adjacent network access.
     */
    AdjacentNetwork = 'ADJACENT_NETWORK',

    /**
     * Denotes that the vulnerability can be exploited with network access (i.e. remotely).
     */
    Network = 'NETWORK',
}

/**
 * An enumeration of possible access complexities associated with a vulnerability.
 *
 * @public
 */
export enum AccessComplexity {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes low access complexity.
     */
    Low = 'LOW',

    /**
     * Denotes medium access complexity.
     */
    Medium = 'MEDIUM',

    /**
     * Denotes high access complexity.
     */
    High = 'HIGH',
}

/**
 * An enumeration of possible authentication requirements associated with a vulnerability.
 *
 * @public
 */
export enum Authentication {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes that multiple authenticated accounts are required to exploit the vulnerability.
     */
    Multiple = 'MULTIPLE',

    /**
     * Denotes that a single authenticated account is required to exploit the vulnerability.
     */
    Single = 'SINGLE',

    /**
     * Denotes that a no authenticated account is required to exploit the vulnerability.
     */
    None = 'NONE',
}

/**
 * An enumeration of possible collateral damage potentials associated with a vulnerability.
 *
 * @public
 */
export enum CollateralDamagePotential {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes that there is no potential for collateral damage stemming from the vulnerability.
     */
    None = 'NONE',

    /**
     * Denotes that there is low potential for collateral damage stemming from the vulnerability.
     */
    Low = 'LOW',

    /**
     * Denotes that there is low-medium potential for collateral damage stemming from the vulnerability.
     */
    LowMedium = 'LOW_MEDIUM',

    /**
     * Denotes that there is medium-high potential for collateral damage stemming from the vulnerability.
     */
    MediumHigh = 'MEDIUM_HIGH',

    /**
     * Denotes that there is high potential for collateral damage stemming from the vulnerability.
     */
    High = 'HIGH',
}

/**
 * An enumeration of possible exploitability levels associated with a vulnerability.
 *
 * @public
 */
export enum Exploitability {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes that it is unproven that an exploit for the vulnerability exists.
     */
    UnprovenThatExplotExists = 'UNPROVEN_THAT_EXPLOIT_EXISTS',

    /**
     * Denotes that proof of concept code for exploiting the vulnerability exists.
     */
    ProofOfConceptCode = 'PROOF_OF_CONCEPT_CODE',

    /**
     * Denotes that a functional exploit for the vulnerability exists.
     */
    FunctionalExploitExists = 'FUNCTIONAL_EXPLOIT_EXISTS',

    /**
     * Denotes that a weaponised exploit for the vulnerability is readily available.
     */
    High = 'HIGH',
}

/**
 * An enumeration of possible impact subscores associated with a vulnerability.
 *
 * @public
 */
export enum ImpactSubscore {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes low impact.
     */
    Low = 'LOW',

    /**
     * Denotes medium impact.
     */
    Medium = 'MEDIUM',

    /**
     * Denotes high impact.
     */
    High = 'HIGH',
}

/**
 * An enumeration of possible impact magnitudes associated with a vulnerability.
 *
 * @public
 */
export enum Impact {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes no impact.
     */
    None = 'NONE',

    /**
     * Denotes partial impact.
     */
    Partial = 'PARTIAL',

    /**
     * Denotes complete impact.
     */
    Complete = 'COMPLETE',
}

/**
 * An enumeration of possible remediation levels associated with a vulnerability.
 *
 * @public
 */
export enum RemediationLevel {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes that an official fix has been released by the vendor for the vulnerability.
     */
    OfficalFix = 'OFFICIAL_FIX',

    /**
     * Denotes that an temporary fix has been released for the vulnerability.
     */
    TemporaryFix = 'TEMPORARY_FIX',

    /**
     * Denotes that a workaround (e.g. configuration change) is available for the vulnerability.
     */
    Workaround = 'WORKAROUND',

    /**
     * Denotes that there is currently no known remediation for the vulnerability.
     */
    Unavailable = 'UNAVAILABLE',
}

/**
 * An enumeration of possible report confidence levels associated with a vulnerability.
 *
 * @public
 */
export enum ReportConfidence {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes that the existence of the vulnerability is unconfirmed.
     */
    Unconfirmed = 'UNCONFIRMED',

    /**
     * Denotes that the existence of the vulnerability is confirmed, but has not been successfully reproduced elsewhere
     * (i.e. corroborated).
     */
    Uncorroborated = 'UNCORROBORATED',

    /**
     * Denotes that the existence of the vulnerability is definitively confirmed.
     */
    Confirmed = 'CONFIRMED',
}

/**
 * An enumeration of possible target distribution levels associated with a vulnerability.
 *
 * @public
 */
export enum TargetDistribution {
    /**
     * Denotes an undefined value.
     */
    NotDefined = 'NOT_DEFINED',

    /**
     * Denotes that no vulnerable targets currently exist.
     */
    None = 'NONE',

    /**
     * Denotes that few vulnerable targets currently exist.
     */
    Low = 'LOW',

    /**
     * Denotes that a significant number vulnerable targets currently exist.
     */
    Medium = 'MEDIUM',

    /**
     * Denotes that vulnerable targets are widespread.
     */
    High = 'HIGH',
}

/**
 * An object containing all enums bundled for export.
 */
export const enums = {
    AccessVector,
    AccessComplexity,
    Authentication,
    CollateralDamagePotential,
    Exploitability,
    ImpactSubscore,
    Impact,
    RemediationLevel,
    ReportConfidence,
    TargetDistribution,
};
